import * as React from "react";
import { TextInputField } from "../TextInputField/TextInputField";
import { SelectOneField } from "../SelectOneField/SelectOneField";
import { AutocompleteField } from "../AutocompleteField/AutocompleteField";
import { DatePickerField } from "../DatePickerField/DatePickerField";
import { sanitizeFieldName } from "../formikUtils";
import { CustomFieldDefinition } from "gen/clients";
import { PipeSeparatedSelectManyField } from "../PipeSeparatedSelectManyField/PipeSeparatedSelectManyField";
import { CheckboxField } from "../CheckboxField/CheckboxField";
import { useFormikContext } from "formik";
import { useEffect } from "react";
import { PersonInfoInputField } from "../PersonInfoInputField/PersonInfoInputField";

interface Props {
  customFieldDefinition: CustomFieldDefinition;
  variant?: "outlined" | "filled" | "standard";
}

const CustomField: React.FC<Props> = ({ customFieldDefinition, variant }) => {
  const { setFieldValue } = useFormikContext();
  const fieldName = sanitizeFieldName(customFieldDefinition.name);

  useEffect(() => {
    return () => {
      setFieldValue(fieldName, "");
    };
  }, [fieldName, setFieldValue]);

  switch (customFieldDefinition.type) {
    case CustomFieldDefinition.type.INPUT_TEXT:
      return (
        <TextInputField
          name={fieldName}
          label={customFieldDefinition.name}
          required={customFieldDefinition.required}
          validationRegex={customFieldDefinition.validationRegex}
          validationMessage={customFieldDefinition.validationMessage}
          helperText={customFieldDefinition.description}
          variant={variant}
          disabled={customFieldDefinition.disabled}
        />
      );
    case CustomFieldDefinition.type.TEXT_AREA:
      return (
        <TextInputField
          name={fieldName}
          label={customFieldDefinition.name}
          required={customFieldDefinition.required}
          validationRegex={customFieldDefinition.validationRegex}
          validationMessage={customFieldDefinition.validationMessage}
          helperText={customFieldDefinition.description}
          variant={variant || "outlined"}
          disabled={customFieldDefinition.disabled}
          multiline={true}
          rows={5}
        />
      );
    case CustomFieldDefinition.type.LISTONLY:
      return (
        <SelectOneField
          name={fieldName}
          label={customFieldDefinition.name}
          options={customFieldDefinition.options || []}
          required={customFieldDefinition.required}
          helperText={customFieldDefinition.description}
          variant={variant}
          disabled={customFieldDefinition.disabled}
        />
      );
    case CustomFieldDefinition.type.LIST:
      return (
        <AutocompleteField
          name={fieldName}
          label={customFieldDefinition.name}
          options={customFieldDefinition.options || []}
          required={customFieldDefinition.required}
          helperText={customFieldDefinition.description}
          variant={variant}
          disabled={customFieldDefinition.disabled}
        />
      );
    case CustomFieldDefinition.type.SELECT_MANY_LIST:
      return (
        <PipeSeparatedSelectManyField
          name={fieldName}
          label={customFieldDefinition.name}
          options={customFieldDefinition.options || []}
          required={customFieldDefinition.required}
          helperText={customFieldDefinition.description}
          disabled={customFieldDefinition.disabled}
        />
      );
    case CustomFieldDefinition.type.DATE:
      return (
        <DatePickerField
          name={fieldName}
          label={customFieldDefinition.name}
          required={customFieldDefinition.required}
          helperText={customFieldDefinition.description}
          variant={variant}
          validationRegex={customFieldDefinition.validationRegex}
          validationMessage={customFieldDefinition.validationMessage}
          disabled={customFieldDefinition.disabled}
        />
      );
    case CustomFieldDefinition.type.CHECKBOX:
      return (
        <CheckboxField
          name={fieldName}
          Label={{
            label: customFieldDefinition.name
          }}
          required={customFieldDefinition.required}
          helperText={customFieldDefinition.description}
          disabled={customFieldDefinition.disabled}
        />
      );
    case CustomFieldDefinition.type.PERSON_INFO:
      return (
        <PersonInfoInputField
          name={fieldName}
          label={customFieldDefinition.name}
          required={customFieldDefinition.required}
          helperText={customFieldDefinition.description}
          variant={variant}
        />
      );
    default:
      return <div>Unknown custom field type: {customFieldDefinition.type}</div>;
  }
};

export { CustomField };
